import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  setDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import TourGuide from "../components/TourGuide";
import { StatSkeleton } from "../components/LoadingSkeleton";
import { notify } from "../utils/notifications";
import { decryptPatient } from "../utils/encryption";

function formatTime(ts) {
  if (!ts) return "";
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  return isNaN(date)
    ? ""
    : date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
}

function initialFromName(name, fallback = "-") {
  if (!name || typeof name !== "string") return fallback;
  return name.trim().charAt(0).toUpperCase() || fallback;
}

function isConfirmed(status) {
  const s = String(status || "").toLowerCase();
  return s.includes("confirm");
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Good Morning";
  if (hour >= 12 && hour < 17) return "Good Afternoon";
  if (hour >= 17 && hour < 21) return "Good Evening";
  return "Good Night";
}

function DoctorDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    today: 0,
    patients: 0,
    active: 0,
    completed: 0,
    pending: 0,
  });
  const [upNext, setUpNext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("Doctor");
  const [notificationCount, setNotificationCount] = useState(0);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser?.displayName) {
      setUserName(currentUser.displayName);
    }
  }, []);

  // Show guided tour on first login only (per user)
  useEffect(() => {
    const checkFirstTimeTour = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const ref = doc(db, "users", currentUser.uid);
        const snap = await getDoc(ref);
        const done =
          snap.exists() && snap.data().onboardingTourCompleted === true;
        if (!done) {
          setShowTour(true);
        }
      } catch (e) {
        console.warn("Failed to check onboarding tour", e);
      }
    };
    checkFirstTimeTour();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      const fetchData = async () => {
        try {
          setLoading(true);
          const patientSnap = await getDocs(
            query(
              collection(db, "patients"),
              where("userId", "==", currentUser.uid)
            )
          );
          const patientData = patientSnap.docs.map((d) =>
            decryptPatient({ id: d.id, ...d.data() }),
          );

          // Calculate patient stats
          const activePatients = patientData.filter((p) => {
            const status = (p.status || "").toLowerCase();
            return status.includes("active");
          });
          const newPatients = patientData.filter((p) => {
            const status = (p.status || "").toLowerCase();
            return status.includes("new");
          });

          setStats((s) => ({
            ...s,
            patients: patientData.length,
            active: activePatients.length,
            pending: newPatients.length,
          }));

          const apptSnap = await getDocs(
            query(
              collection(db, "appointments"),
              where("userId", "==", currentUser.uid)
            )
          );
          const now = Date.now();
          const appts = apptSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          // Sort appointments by start time locally
          appts.sort((a, b) => {
            const aTime = a.start?.toMillis ? a.start.toMillis() : 0;
            const bTime = b.start?.toMillis ? b.start.toMillis() : 0;
            return aTime - bTime;
          });

          const upcoming = appts
            .map((a) => ({
              ...a,
              startMs: a.start?.toMillis
                ? a.start.toMillis()
                : Date.parse(a.start),
            }))
            .filter(
              (a) => a.startMs && a.startMs >= now && isConfirmed(a.status)
            )
            .sort((a, b) => a.startMs - b.startMs);
          const today = appts.filter((a) => {
            const date = a.start?.toDate ? a.start.toDate() : new Date(a.start);
            if (isNaN(date)) return false;
            const todayStr = new Date().toDateString();
            return date.toDateString() === todayStr && isConfirmed(a.status);
          });

          // Calculate completed appointments this week
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          const completed = appts.filter((a) => {
            const status = (a.status || "").toLowerCase();
            return status.includes("complete");
          });

          setStats((s) => ({
            ...s,
            today: today.length,
            completed: completed.length,
          }));
          setUpNext(upcoming[0] || null);
        } catch (e) {
          console.warn("Failed to load dashboard data", e);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const checkAndCreateNotifications = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const now = Date.now();

        // Get all appointments
        const apptSnap = await getDocs(
          query(
            collection(db, "appointments"),
            where("userId", "==", currentUser.uid)
          )
        );

        // Get existing notifications
        const notifSnap = await getDocs(
          query(
            collection(db, "notifications"),
            where("userId", "==", currentUser.uid)
          )
        );

        const existingNotifs = notifSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // Only count unread on the latest notification per appointment
        const latestByKey = new Map();
        existingNotifs.forEach((n) => {
          const key = n.appointmentId || n.id;
          const time = n.createdAt?.toMillis ? n.createdAt.toMillis() : 0;
          const prev = latestByKey.get(key);
          const prevTime = prev?.createdAt?.toMillis
            ? prev.createdAt.toMillis()
            : 0;
          if (!prev || time >= prevTime) {
            latestByKey.set(key, n);
          }
        });
        const unreadCount = Array.from(latestByKey.values()).filter(
          (n) => n.read === false
        ).length;
        setNotificationCount(unreadCount);

        const existingApptIds = new Set(
          existingNotifs.map((n) => n.appointmentId)
        );
        // Also track ended notifications by checking notification IDs
        const existingEndedNotifs = new Set(
          existingNotifs
            .filter((n) => n.id && n.id.includes("__ended"))
            .map((n) => {
              // Extract appointment ID from notification ID like "uid__apptId__ended"
              const parts = n.id.split("__");
              return parts.length >= 2 ? parts[1] : null;
            })
            .filter((id) => id !== null)
        );

        // Check for appointments that should trigger notifications
        for (const apptDoc of apptSnap.docs) {
          const appt = { id: apptDoc.id, ...apptDoc.data() };
          const startTime = appt.start?.toMillis
            ? appt.start.toMillis()
            : Date.parse(appt.start);

          if (!startTime || isNaN(startTime)) continue;

          const timeDiff = now - startTime;
          const withinStartWindow = timeDiff >= 0 && timeDiff <= 5 * 60 * 1000;
          // Check if appointment time has passed (5 minutes to 1 hour after appointment time)
          const appointmentEnded =
            timeDiff >= 5 * 60 * 1000 && timeDiff <= 60 * 60 * 1000;

          // Create notification if appointment just started (idempotent per appointment)
          if (
            withinStartWindow &&
            !existingApptIds.has(appt.id) &&
            appt.status?.toLowerCase() === "confirmed"
          ) {
            const notifId = `${currentUser.uid}__${appt.id}`;
            const notifRef = doc(collection(db, "notifications"), notifId);
            const exists = await getDoc(notifRef);
            if (!exists.exists()) {
              await notify(
                currentUser.uid,
                {
                  id: notifId,
                  type: "started",
                  appointmentId: appt.id,
                  patientName: appt.patientName || "Patient",
                  reason: appt.reason || "Appointment",
                  startTime: appt.start,
                  title: "Appointment Started",
                  message: `Appointment with ${
                    appt.patientName || "patient"
                  } has started`,
                },
                false
              ); // Don't send browser notification for started appointments
            }
          }

          // Check for appointments that just ended (5 minutes to 1 hour after appointment time)
          if (
            appointmentEnded &&
            appt.status?.toLowerCase() === "confirmed" &&
            !existingEndedNotifs.has(appt.id)
          ) {
            const endedNotifId = `${currentUser.uid}__${appt.id}__ended`;
            const endedNotifRef = doc(
              collection(db, "notifications"),
              endedNotifId
            );
            const endedExists = await getDoc(endedNotifRef);
            if (!endedExists.exists()) {
              await notify(currentUser.uid, {
                id: endedNotifId,
                type: "appointment_ended",
                appointmentId: appt.id,
                patientName: appt.patientName || "Patient",
                title: "Appointment Time Over",
                message: `Appointment with ${
                  appt.patientName || "patient"
                } has ended. Please mark as complete or not complete.`,
              });
            }
          }

          // Check for appointments that passed without completion (more than 2 hours ago)
          const twoHoursAgo = now - 2 * 60 * 60 * 1000;
          const passedWithoutCompletion =
            timeDiff > 0 &&
            timeDiff > twoHoursAgo &&
            appt.status?.toLowerCase() !== "completed" &&
            appt.status?.toLowerCase() !== "cancelled" &&
            appt.status?.toLowerCase() !== "not_completed";

          if (passedWithoutCompletion) {
            const incompleteNotifId = `${currentUser.uid}__${appt.id}__incomplete`;
            const incompleteNotifRef = doc(
              collection(db, "notifications"),
              incompleteNotifId
            );
            const incompleteExists = await getDoc(incompleteNotifRef);
            if (!incompleteExists.exists()) {
              await notify(currentUser.uid, {
                id: incompleteNotifId,
                type: "appointment_incomplete",
                appointmentId: appt.id,
                patientName: appt.patientName || "Patient",
                title: "Appointment Not Completed",
                message: `Appointment with ${
                  appt.patientName || "patient"
                } has passed without being marked as completed.`,
              });
            }
          }
        }
      } catch (error) {
        console.error("Error checking notifications:", error);
      }
    };

    checkAndCreateNotifications();
    const interval = setInterval(checkAndCreateNotifications, 30000); // Check every 30 seconds

    // Also check when page becomes visible (user returns from notifications page)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAndCreateNotifications();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <div className="dash-layout">
      <header className="dash-top">
        <div className="dash-top-left">
          <div
            className="dash-avatar"
            aria-label="Doctor avatar"
            onClick={() => navigate("/settings")}
            style={{ cursor: "pointer" }}
          >
            <span>{initialFromName(userName, "D")}</span>
          </div>
          <div className="dash-greet-block">
            <p className="dash-sub">{getGreeting()},</p>
            <h1 className="dash-title">{userName}</h1>
          </div>
        </div>
        <button
          onClick={() => navigate("/notifications")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "8px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            color: "#64748b",
          }}
          aria-label="Notifications"
        >
          <span className="material-symbols-outlined">notifications</span>
          {notificationCount > 0 && (
            <span
              style={{
                position: "absolute",
                top: "4px",
                right: "4px",
                background: "#ef4444",
                color: "white",
                borderRadius: "10px",
                padding: "2px 6px",
                fontSize: "10px",
                fontWeight: 600,
                minWidth: "16px",
                textAlign: "center",
              }}
            >
              {notificationCount}
            </span>
          )}
        </button>
      </header>

      <section className="dash-stats">
        {loading ? (
          <>
            <StatSkeleton />
            <StatSkeleton />
            <StatSkeleton />
            <StatSkeleton />
          </>
        ) : (
          <>
            <div
              className="dash-stat-card"
              onClick={() =>
                navigate("/appointments", { state: { focusToday: true } })
              }
              style={{ cursor: "pointer" }}
            >
              <div className="dash-stat-head">
                <span className="material-symbols-outlined dash-stat-icon">
                  calendar_today
                </span>
                <p className="dash-stat-label">Today's Appointments</p>
              </div>
              <p className="dash-stat-value">{stats.today}</p>
              <p className="dash-stat-sublabel">Confirmed</p>
            </div>
            <div
              className="dash-stat-card"
              onClick={() =>
                navigate("/patients", { state: { filterStatus: "Active" } })
              }
              style={{ cursor: "pointer" }}
            >
              <div className="dash-stat-head">
                <span className="material-symbols-outlined dash-stat-icon success">
                  groups
                </span>
                <p className="dash-stat-label">Active Patients</p>
              </div>
              <p className="dash-stat-value">{stats.active}</p>
              <p className="dash-stat-sublabel">In treatment</p>
            </div>
            <div className="dash-stat-card">
              <div className="dash-stat-head">
                <span className="material-symbols-outlined dash-stat-icon info">
                  calendar_add_on
                </span>
                <p className="dash-stat-label">New Appointment</p>
              </div>
              <button
                className="dash-btn-primary"
                onClick={() => navigate("/appointments/new")}
                style={{ marginTop: 8 }}
              >
                <span className="material-symbols-outlined">add</span>
                Create
              </button>
            </div>
            <div className="dash-stat-card">
              <div className="dash-stat-head">
                <span className="material-symbols-outlined dash-stat-icon warning">
                  person_add
                </span>
                <p className="dash-stat-label">New Patient</p>
              </div>
              <button
                className="dash-btn-primary"
                onClick={() => navigate("/patients/new")}
                style={{ marginTop: 8 }}
              >
                <span className="material-symbols-outlined">add</span>
                Add
              </button>
            </div>
          </>
        )}
      </section>

      <section className="dash-upnext">
        <div className="dash-upnext-head">
          <h3>Next Appointment</h3>
          {upNext ? (
            <span className="dash-time-tag">{formatTime(upNext.start)}</span>
          ) : (
            <span className="dash-time-tag" style={{ opacity: 0.6 }}>
              None scheduled
            </span>
          )}
        </div>
        {upNext ? (
          <div
            className="dash-upnext-card"
            onClick={() => upNext.id && navigate(`/appointments/${upNext.id}`)}
          >
            <div className="dash-upnext-main">
              <div className="dash-upnext-info">
                <h4>{upNext.patientName || "Unknown Patient"}</h4>
                <div className="dash-upnext-meta">
                  <span className="material-symbols-outlined">
                    medical_services
                  </span>
                  <span>{upNext.reason || "Consultation"}</span>
                </div>
                {upNext.start &&
                  (() => {
                    const startTime = upNext.start?.toMillis
                      ? upNext.start.toMillis()
                      : Date.parse(upNext.start);
                    const now = Date.now();
                    const diff = startTime - now;
                    const minutes = Math.floor(diff / 60000);
                    if (minutes > 0) {
                      return (
                        <div className="dash-upnext-time-remaining">
                          In {minutes} min
                        </div>
                      );
                    } else if (minutes > -60) {
                      return (
                        <div className="dash-upnext-time-remaining active">
                          Started {Math.abs(minutes)} min ago
                        </div>
                      );
                    }
                    return null;
                  })()}
              </div>
              <div className="dash-upnext-actions">
                <button
                  className="dash-btn-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (upNext.id) navigate(`/appointments/${upNext.id}`);
                  }}
                >
                  <span className="material-symbols-outlined">play_circle</span>
                  View Details
                </button>
              </div>
            </div>
            <div className="dash-upnext-blob" />
          </div>
        ) : (
          <div className="dash-upnext-empty">
            <span className="material-symbols-outlined">event_available</span>
            <p>No upcoming appointments</p>
            <button
              className="dash-btn-primary"
              onClick={() => navigate("/appointments/new")}
            >
              <span className="material-symbols-outlined">add</span>
              Schedule Appointment
            </button>
          </div>
        )}
      </section>

      <nav className="dash-bottom-nav">
        <button
          className="dash-nav-item active"
          onClick={() => navigate("/doctor")}
        >
          <span className="material-symbols-outlined">home</span>
          <span>Home</span>
        </button>
        <button
          className="dash-nav-item"
          onClick={() => navigate("/appointments")}
        >
          <span className="material-symbols-outlined">calendar_month</span>
          <span>Appointments</span>
        </button>
        <button className="dash-nav-item" onClick={() => navigate("/patients")}>
          <span className="material-symbols-outlined">groups</span>
          <span>Patients</span>
        </button>
        <button className="dash-nav-item" onClick={() => navigate("/reports")}>
          <span className="material-symbols-outlined">assessment</span>
          <span>Reports</span>
        </button>
      </nav>

      {showTour && (
        <TourGuide
          onClose={async () => {
            setShowTour(false);
            try {
              const currentUser = auth.currentUser;
              if (currentUser) {
                const ref = doc(db, "users", currentUser.uid);
                await setDoc(
                  ref,
                  { onboardingTourCompleted: true },
                  { merge: true }
                );
              }
            } catch (e) {
              console.warn("Failed to mark onboarding tour complete", e);
            }
          }}
          steps={[
            {
              selector: ".dash-bottom-nav .dash-nav-item:nth-child(1)",
              title: "Home",
              description:
                "Your dashboard with stats, schedule, and quick actions.",
            },
            {
              selector: ".dash-bottom-nav .dash-nav-item:nth-child(2)",
              title: "Schedule",
              description:
                "View and manage all appointments, filter, and search.",
            },
            {
              selector: ".dash-bottom-nav .dash-nav-item:nth-child(3)",
              title: "Patients",
              description: "Browse patients, update status, and edit profiles.",
            },
            {
              selector: ".dash-bottom-nav .dash-nav-item:nth-child(4)",
              title: "Reports",
              description:
                "View analytics, insights, and reports for your practice.",
            },
          ]}
        />
      )}
    </div>
  );
}

export default DoctorDashboard;
